import React, { useState, useEffect, useCallback } from 'react';
import { Activity, AlertTriangle, CheckCircle, XCircle, RefreshCw, Server, Wifi, Brain, Database, Radio } from 'lucide-react';
import { apiUtils } from '../../../utils/apiUtils';

const SystemHealthMonitor = () => {
	const [detailedHealth, setDetailedHealth] = useState(null);
	const [openAIHealth, setOpenAIHealth] = useState(null);
	const [jobHealth, setJobHealth] = useState(null);
	const [connectionStats, setConnectionStats] = useState(null);
	const [loading, setLoading] = useState(true);
	const [errors, setErrors] = useState({});
	const [lastCheck, setLastCheck] = useState(null);

	const fetchDetailedHealth = async () => {
		try {
			const response = await apiUtils.get('/api/health/detailed');
			const data = await response.json();
			if (data.status) {
				setDetailedHealth(data);
			} else {
				throw new Error('Invalid detailed health response');
			}
			return { success: true };
		} catch (err) {
			console.error('Error fetching detailed health:', err);
			setErrors(prev => ({ ...prev, detailed: err.message || 'Failed to fetch detailed health' }));
			return { success: false, error: err.message };
		}
	};

	const fetchOpenAIHealth = async () => {
		try {
			const response = await apiUtils.get('/api/health/openai');
			const data = await response.json();
			setOpenAIHealth(data);
			return { success: true };
		} catch (err) {
			console.error('Error fetching OpenAI health:', err);
			setErrors(prev => ({ ...prev, openai: err.message || 'Failed to fetch OpenAI health' }));
			setOpenAIHealth({ status: 'error', error: err.message || 'Connection failed' });
			return { success: false, error: err.message };
		}
	};

	const fetchJobHealth = async () => {
		try {
			const response = await apiUtils.get('/api/async-job-health');
			const data = await response.json();
			if (data.success) {
				setJobHealth(data.data);
			} else {
				throw new Error(data.error || 'Invalid job health response');
			}
			return { success: true };
		} catch (err) {
			console.error('Error fetching job health:', err);
			setErrors(prev => ({ ...prev, jobs: err.message || 'Failed to fetch job health' }));
			return { success: false, error: err.message };
		}
	};

	const fetchConnectionStats = async () => {
		try {
			const response = await apiUtils.get('/api/scans/health/connections');
			const data = await response.json();
			if (data.success && data.data) {
				setConnectionStats(data.data);
			} else {
				throw new Error('Invalid connection stats response');
			}
			return { success: true };
		} catch (err) {
			console.error('Error fetching connection stats:', err);
			setErrors(prev => ({ ...prev, connections: err.message || 'Failed to fetch connection stats' }));
			return { success: false, error: err.message };
		}
	};

	const checkHealth = useCallback(async () => {
		setLoading(true);
		setErrors({});

		await Promise.all([
			fetchDetailedHealth(),
			fetchOpenAIHealth(),
			fetchJobHealth(),
			fetchConnectionStats()
		]);

		setLastCheck(new Date());
		setLoading(false);
	}, []);

	useEffect(() => {
		checkHealth();

		// Auto-refresh every 60 seconds
		const interval = setInterval(() => {
			if (!document.hidden) checkHealth();
		}, 60000);

		return () => clearInterval(interval);
	}, [checkHealth]);

	const getStatusIndicator = (status) => {
		switch (status) {
			case 'healthy':
			case 'active':
			case 'connected':
			case 'online':
				return <CheckCircle className="w-5 h-5 text-green-600" />;
			case 'warning':
			case 'degraded':
				return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
			case 'error':
			case 'failed':
			case 'disconnected':
			case 'offline':
				return <XCircle className="w-5 h-5 text-red-600" />;
			default:
				return <Activity className="w-5 h-5 text-muted-foreground" />;
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case 'healthy':
			case 'active':
			case 'connected':
			case 'online':
				return 'text-green-600';
			case 'warning':
			case 'degraded':
				return 'text-yellow-600';
			case 'error':
			case 'failed':
			case 'disconnected':
			case 'offline':
				return 'text-red-600';
			default:
				return 'text-muted-foreground';
		}
	};

	const getStatusText = (status, fallback = 'Unknown') => {
		switch (status) {
			case 'healthy': return 'Healthy';
			case 'active': return 'Active';
			case 'connected': return 'Connected';
			case 'online': return 'Online';
			case 'degraded': return 'Degraded';
			case 'warning': return 'Warning';
			case 'error': return 'Error';
			case 'failed': return 'Failed';
			case 'disconnected': return 'Disconnected';
			case 'offline': return 'Offline';
			default: return fallback;
		}
	};

	const formatBytes = (bytes) => {
		if (bytes === null || bytes === undefined || bytes < 0) return 'N/A';
		if (bytes === 0) return '0 B';
		const sizes = ['B', 'KB', 'MB', 'GB'];
		const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
		return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
	};

	const formatUptime = (seconds) => {
		if (!seconds && seconds !== 0) return 'N/A';
		const hrs = Math.floor(seconds / 3600);
		const mins = Math.floor((seconds % 3600) / 60);
		return `${hrs}h ${mins}m`;
	};

	if (loading && !detailedHealth) {
		return (
			<div className="bg-card rounded-lg shadow-sm border border-border">
				<div className="p-6 border-b border-border">
					<h3 className="text-lg font-semibold text-foreground">System Health Monitor</h3>
				</div>
				<div className="p-6">
					<div className="flex items-center justify-center py-8">
						<RefreshCw className="w-6 h-6 animate-spin text-brand-600 mr-2" />
						<span className="text-muted-foreground">Checking system health...</span>
					</div>
				</div>
			</div>
		);
	}

	// Determine overall system status
	const overallStatus = detailedHealth?.status || 'unknown';
	const hasErrors = Object.keys(errors).length > 0;

	return (
		<div className="bg-card rounded-lg shadow-sm border border-border">
			<div className="p-6 border-b border-border">
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-3">
						<h3 className="text-lg font-semibold text-foreground">System Health Monitor</h3>
						<span className={`px-2 py-1 text-xs font-medium rounded-full ${
							overallStatus === 'healthy' ? 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300' :
							overallStatus === 'degraded' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300' :
							'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300'
						}`}>
							{getStatusText(overallStatus, 'Unknown').toUpperCase()}
						</span>
					</div>
					<div className="flex items-center space-x-4">
						{lastCheck && (
							<span className="text-sm text-muted-foreground">
								Last check: {lastCheck.toLocaleTimeString()}
							</span>
						)}
						<button
							onClick={checkHealth}
							disabled={loading}
							className="flex items-center space-x-2 bg-brand-600 text-white px-3 py-1 rounded-md hover:bg-brand-700 disabled:opacity-50"
						>
							<RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
							<span>Check</span>
						</button>
					</div>
				</div>
			</div>

			<div className="p-6">
				{hasErrors && (
					<div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 dark:bg-red-500/10 dark:border-red-500/30">
						<div className="flex items-center mb-2">
							<AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
							<span className="text-red-800 font-medium">Some health checks failed</span>
						</div>
						<ul className="text-sm text-red-700 list-disc list-inside">
							{Object.entries(errors).map(([key, msg]) => (
								<li key={key}>{key}: {msg}</li>
							))}
						</ul>
					</div>
				)}

				{/* Backend Services */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
					<div className="space-y-4">
						<h4 className="font-medium text-foreground flex items-center">
							<Server className="w-5 h-5 mr-2" />
							Backend Services
						</h4>

						<div className="space-y-3">
							{/* Main Backend */}
							<div className="flex items-center justify-between p-3 bg-muted rounded-lg">
								<div className="flex items-center space-x-3">
									<div className="flex items-center space-x-2">
										{getStatusIndicator(detailedHealth?.status === 'healthy' ? 'healthy' : detailedHealth?.status)}
										<span className="font-medium">Main Backend</span>
									</div>
								</div>
								<div className="text-right">
									<div className={`font-medium ${getStatusColor(detailedHealth?.status)}`}>
										{getStatusText(detailedHealth?.status, 'Unknown')}
									</div>
									<div className="text-xs text-muted-foreground">
										Uptime: {formatUptime(detailedHealth?.uptime)}
									</div>
								</div>
							</div>

							{/* MongoDB */}
							<div className="flex items-center justify-between p-3 bg-muted rounded-lg">
								<div className="flex items-center space-x-3">
									<div className="flex items-center space-x-2">
										{getStatusIndicator(detailedHealth?.services?.mongodb?.status)}
										<span className="font-medium">MongoDB</span>
									</div>
								</div>
								<div className="text-right">
									<div className={`font-medium ${getStatusColor(detailedHealth?.services?.mongodb?.status)}`}>
										{getStatusText(detailedHealth?.services?.mongodb?.status, 'Unknown')}
									</div>
									<div className="text-xs text-muted-foreground">
										ReadyState: {detailedHealth?.services?.mongodb?.readyState ?? 'N/A'}
									</div>
								</div>
							</div>

							{/* Redis */}
							<div className="flex items-center justify-between p-3 bg-muted rounded-lg">
								<div className="flex items-center space-x-3">
									<div className="flex items-center space-x-2">
										{getStatusIndicator(detailedHealth?.services?.redis?.status)}
										<span className="font-medium">Redis</span>
									</div>
								</div>
								<div className="text-right">
									<div className={`font-medium ${getStatusColor(detailedHealth?.services?.redis?.status)}`}>
										{getStatusText(detailedHealth?.services?.redis?.status, 'Unknown')}
									</div>
									{detailedHealth?.services?.redis?.error && (
										<div className="text-xs text-red-500">
											{detailedHealth.services.redis.error}
										</div>
									)}
								</div>
							</div>

							{/* Analysis Microservice */}
							<div className="flex items-center justify-between p-3 bg-muted rounded-lg">
								<div className="flex items-center space-x-3">
									<div className="flex items-center space-x-2">
										{getStatusIndicator(detailedHealth?.services?.analysisMicroservice?.status)}
										<span className="font-medium">Analysis Microservice</span>
									</div>
								</div>
								<div className="text-right">
									<div className={`font-medium ${getStatusColor(detailedHealth?.services?.analysisMicroservice?.status)}`}>
										{getStatusText(detailedHealth?.services?.analysisMicroservice?.status, 'Unknown')}
									</div>
									{detailedHealth?.services?.analysisMicroservice?.error && (
										<div className="text-xs text-red-500">
											{detailedHealth.services.analysisMicroservice.error}
										</div>
									)}
								</div>
							</div>
						</div>
					</div>

					{/* External Services */}
					<div className="space-y-4">
						<h4 className="font-medium text-foreground flex items-center">
							<Wifi className="w-5 h-5 mr-2" />
							External Services
						</h4>

						<div className="space-y-3">
							{/* Scraper Microservice */}
							<div className="flex items-center justify-between p-3 bg-muted rounded-lg">
								<div className="flex items-center space-x-3">
									<div className="flex items-center space-x-2">
										{getStatusIndicator(detailedHealth?.services?.scraper?.status)}
										<span className="font-medium">Scraper Service</span>
									</div>
								</div>
								<div className="text-right">
									<div className={`font-medium ${getStatusColor(detailedHealth?.services?.scraper?.status)}`}>
										{getStatusText(detailedHealth?.services?.scraper?.status, 'Unknown')}
									</div>
									<div className="text-xs text-muted-foreground">
										{detailedHealth?.services?.scraper?.data?.status || 'No data'}
									</div>
								</div>
							</div>

							{/* OpenAI API */}
							<div className="flex items-center justify-between p-3 bg-muted rounded-lg">
								<div className="flex items-center space-x-3">
									<div className="flex items-center space-x-2">
										{getStatusIndicator(openAIHealth?.status)}
										<span className="font-medium">OpenAI API</span>
									</div>
								</div>
								<div className="text-right">
									<div className={`font-medium ${getStatusColor(openAIHealth?.status)}`}>
										{openAIHealth?.status === 'healthy' ? 'Connected' : getStatusText(openAIHealth?.status, 'Unknown')}
									</div>
									<div className="text-xs text-muted-foreground">
										{openAIHealth?.status === 'healthy' ? 'Operational' : ''}
									</div>
									{openAIHealth?.error && (
										<div className="text-xs text-red-500 max-w-[200px] truncate">
											{openAIHealth.error}
										</div>
									)}
								</div>
							</div>

							{/* Queue System */}
							<div className="flex items-center justify-between p-3 bg-muted rounded-lg">
								<div className="flex items-center space-x-3">
									<div className="flex items-center space-x-2">
										{getStatusIndicator(jobHealth?.autoRecoveryRunning ? 'active' : 'warning')}
										<span className="font-medium">Job Queue System</span>
									</div>
								</div>
								<div className="text-right">
									<div className={`font-medium ${getStatusColor(jobHealth?.autoRecoveryRunning ? 'active' : 'warning')}`}>
										{jobHealth?.autoRecoveryRunning ? 'Active' : 'Paused'}
									</div>
									<div className="text-xs text-muted-foreground">
										{jobHealth?.queueHealth?.total !== undefined ? `${jobHealth.queueHealth.total} total jobs` : 'No data'}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* System Metrics */}
				<div className="mt-6 pt-6 border-t border-border">
					<h4 className="font-medium text-foreground flex items-center mb-4">
						<Activity className="w-5 h-5 mr-2" />
						System Metrics
					</h4>

					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						<div className="text-center p-4 bg-muted rounded-lg">
							<div className="text-2xl font-bold text-foreground">{formatUptime(detailedHealth?.uptime)}</div>
							<div className="text-sm text-muted-foreground">Backend Uptime</div>
						</div>
						<div className="text-center p-4 bg-muted rounded-lg">
							<div className="text-2xl font-bold text-foreground">{formatBytes(detailedHealth?.memory?.heapUsed)}</div>
							<div className="text-sm text-muted-foreground">Heap Used</div>
						</div>
						<div className="text-center p-4 bg-muted rounded-lg">
							<div className="text-2xl font-bold text-foreground">{connectionStats?.totalClients ?? 0}</div>
							<div className="text-sm text-muted-foreground">WS Clients</div>
						</div>
						<div className="text-center p-4 bg-muted rounded-lg">
							<div className="text-2xl font-bold text-foreground">{connectionStats?.totalTopics ?? 0}</div>
							<div className="text-sm text-muted-foreground">WS Topics</div>
						</div>
					</div>
				</div>

				{/* Memory Details */}
				{detailedHealth?.memory && (
					<div className="mt-6 pt-6 border-t border-border">
						<h4 className="font-medium text-foreground flex items-center mb-4">
							<Database className="w-5 h-5 mr-2" />
							Memory Usage
						</h4>
						<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
							<div className="text-center p-4 bg-muted rounded-lg">
								<div className="text-lg font-bold text-foreground">{formatBytes(detailedHealth.memory.rss)}</div>
								<div className="text-sm text-muted-foreground">RSS</div>
							</div>
						<div className="text-center p-4 bg-muted rounded-lg">
							<div className="text-lg font-bold text-foreground">{formatBytes(detailedHealth.memory.heapTotal)}</div>
							<div className="text-sm text-muted-foreground">Heap Total</div>
						</div>
							<div className="text-center p-4 bg-muted rounded-lg">
								<div className="text-lg font-bold text-foreground">{formatBytes(detailedHealth.memory.heapUsed)}</div>
								<div className="text-sm text-muted-foreground">Heap Used</div>
							</div>
							<div className="text-center p-4 bg-muted rounded-lg">
								<div className="text-lg font-bold text-foreground">{formatBytes(detailedHealth.memory.external)}</div>
								<div className="text-sm text-muted-foreground">External</div>
							</div>
						</div>
					</div>
				)}

				{/* WebSocket Details */}
				{connectionStats?.activeConnections && Object.keys(connectionStats.activeConnections).length > 0 && (
					<div className="mt-6 pt-6 border-t border-border">
						<h4 className="font-medium text-foreground flex items-center mb-4">
							<Radio className="w-5 h-5 mr-2" />
							WebSocket Subscriptions
						</h4>
						<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
							{Object.entries(connectionStats.activeConnections).map(([topic, count]) => (
								<div key={topic} className="text-center p-4 bg-muted rounded-lg">
									<div className="text-lg font-bold text-foreground">{count}</div>
									<div className="text-sm text-muted-foreground truncate">{topic}</div>
								</div>
							))}
						</div>
					</div>
				)}

				{/* OpenAI Details */}
				{openAIHealth?.status === 'healthy' && (
					<div className="mt-6 pt-6 border-t border-border">
						<h4 className="font-medium text-foreground flex items-center mb-4">
							<Brain className="w-5 h-5 mr-2" />
							OpenAI Details
						</h4>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="p-4 bg-muted rounded-lg">
								<div className="text-sm text-muted-foreground">Status</div>
								<div className="font-medium text-foreground">{openAIHealth?.status === 'healthy' ? 'Operational' : 'Unavailable'}</div>
							</div>
							<div className="p-4 bg-muted rounded-lg">
								<div className="text-sm text-muted-foreground">Response Preview</div>
								<div className="font-medium text-foreground truncate">{openAIHealth.response ? 'Connection test successful' : 'N/A'}</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default SystemHealthMonitor;
